/**
 * React hook for RWA service
 *
 * Provides reactive state management for RWA contract data with role-based access.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getSDK } from '@stratos-wallet/sdk';
import {
  RWAService,
  type RWAAsset,
  type RWAHolding,
  type PaymentBalance,
  type Listing,
  type ListingProposal,
  type PaymentDepositReq,
  type Trade,
  type InvestorProfile,
  type UserRoles,
  type AssetCategory,
  type AssetType,
  type ComplianceConfig,
  type UnderlyingAsset,
  type PaymentToken,
} from './rwa-service';

interface UseRWAOptions {
  packageId: string;
  operatorParty?: string;  // Optional - will be fetched from platform if not provided
  publicParty?: string;    // Public party for visibility
  autoRefresh?: boolean;
  refreshInterval?: number;
}

interface IssuerLicense {
  contractId: string;
  issuer: string;
  issuerName: string;
  allowedCategories: string[];
}

interface AssetProposal {
  contractId: string;
  issuer: string;
  assetId: string;
  name: string;
  category: string;
  assetType: string;
  totalSupply: number;
}

interface UseRWAState {
  // Connection state
  connected: boolean;
  partyId: string | null;
  loading: boolean;
  dataLoaded: boolean;  // True after first successful data load
  error: string | null;

  // Role state
  roles: UserRoles | null;

  // Contract data (investor)
  assets: RWAAsset[];
  myHoldings: RWAHolding[];
  myPaymentBalance: number;
  myPaymentHoldings: PaymentBalance[];
  listings: Listing[];
  myTrades: Trade[];
  myProfile: InvestorProfile | null;

  // Operator data
  issuerLicenses: IssuerLicense[];
  assetProposals: AssetProposal[];
  listingProposals: ListingProposal[];
  depositRequests: PaymentDepositReq[];
  pendingTrades: Trade[];
  allInvestorProfiles: InvestorProfile[];

  // Issuer data
  myIssuedAssets: RWAAsset[];

  // Platform
  platformContractId: string | null;
  platformInitialized: boolean;

  // Actions - Setup
  initializePlatform: (config: { platformName: string; platformFeePercent: string; feeCollector: string; operator: string; publicParty: string }) => Promise<string>;
  selfRegisterAsInvestor: (operatorParty: string, jurisdiction?: string) => Promise<string>;

  // Actions - Common
  refresh: () => Promise<void>;

  // Actions - Investor
  transferTokens: (holdingContractId: string, recipient: string, amount: number) => Promise<string>;
  createListingProposal: (
    holdingContractId: string,
    asset: { assetId: string; assetSymbol: string; assetName: string; description: string; imageUrl?: string },
    amount: number,
    pricePerToken: number,
    paymentToken: PaymentToken
  ) => Promise<string>;
  purchaseFromListing: (listingContractId: string, paymentContractId: string, amount: number) => Promise<{ tradeId: string }>;
  delistAsset: (listingContractId: string, sellerHoldingCid: string, amount: number) => Promise<void>;
  withdrawListingProposal: (proposalContractId: string, holdingContractId: string, amount: number) => Promise<void>;
  requestPaymentDeposit: (token: PaymentToken, amount: number) => Promise<string>;
  unlockTokens: (holdingContractId: string, amount: number) => Promise<void>;

  // Actions - Operator
  appointIssuer: (issuerParty: string, issuerName: string, allowedCategories: AssetCategory[]) => Promise<string>;
  revokeIssuer: (issuerLicenseContractId: string) => Promise<void>;
  approveAssetProposal: (proposalContractId: string) => Promise<string>;
  rejectAssetProposal: (proposalContractId: string, reason: string) => Promise<void>;
  approveListingProposal: (proposalContractId: string) => Promise<string>;
  rejectListingProposal: (proposalContractId: string, reason: string) => Promise<void>;
  approvePaymentDeposit: (requestContractId: string) => Promise<string>;
  settleTrade: (tradeContractId: string, sellerPaymentContractId: string) => Promise<string>;
  autoSettleTrades: () => Promise<number>;
  archiveStuckTrade: (tradeContractId: string) => Promise<void>;

  // Actions - Issuer
  proposeAsset: (assetDetails: {
    assetId: string;
    name: string;
    symbol: string;
    category: AssetCategory;
    assetType: AssetType;
    totalSupply: number;
    compliance: Partial<ComplianceConfig>;
    underlying: Partial<UnderlyingAsset>;
  }) => Promise<string>;
  mintTokens: (assetContractId: string, recipient: string, amount: number) => Promise<string>;
  mintNFT: (assetContractId: string, recipient: string) => Promise<string>;

  // Actions - Verifier
  verifyInvestor: (
    investorParty: string,
    status: 'Unverified' | 'KYCVerified' | 'AccreditedVerified' | 'InstitutionalVerified',
    jurisdiction: string,
    expiryDate?: string
  ) => Promise<string>;
}

export function useRWA(options: UseRWAOptions): UseRWAState {
  const {
    packageId,
    operatorParty: operatorPartyOption,
    publicParty: publicPartyOption,
    autoRefresh = true,
    refreshInterval = 30000,
  } = options;

  const [connected, setConnected] = useState(false);
  const [partyId, setPartyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);  // True after first successful data load
  const [error, setError] = useState<string | null>(null);

  // Operator party (fetched from platform if not provided)
  const [operatorParty, setOperatorParty] = useState<string | null>(operatorPartyOption || null);
  // Public party for visibility
  const [publicParty, setPublicParty] = useState<string | null>(publicPartyOption || null);

  // Sync with options when they change
  useEffect(() => {
    if (operatorPartyOption) setOperatorParty(operatorPartyOption);
  }, [operatorPartyOption]);

  useEffect(() => {
    if (publicPartyOption) setPublicParty(publicPartyOption);
  }, [publicPartyOption]);

  // Role state
  const [roles, setRoles] = useState<UserRoles | null>(null);

  // Investor data
  const [assets, setAssets] = useState<RWAAsset[]>([]);
  const [myHoldings, setMyHoldings] = useState<RWAHolding[]>([]);
  const [myPaymentBalance, setMyPaymentBalance] = useState(0);
  const [myPaymentHoldings, setMyPaymentHoldings] = useState<PaymentBalance[]>([]);
  const [listings, setListings] = useState<Listing[]>([]);
  const [myTrades, setMyTrades] = useState<Trade[]>([]);
  const [myProfile, setMyProfile] = useState<InvestorProfile | null>(null);

  // Operator data
  const [issuerLicenses, setIssuerLicenses] = useState<IssuerLicense[]>([]);
  const [assetProposals, setAssetProposals] = useState<AssetProposal[]>([]);
  const [listingProposals, setListingProposals] = useState<ListingProposal[]>([]);
  const [depositRequests, setDepositRequests] = useState<PaymentDepositReq[]>([]);
  const [pendingTrades, setPendingTrades] = useState<Trade[]>([]);
  const [allInvestorProfiles, setAllInvestorProfiles] = useState<InvestorProfile[]>([]);

  // Issuer data
  const [myIssuedAssets, setMyIssuedAssets] = useState<RWAAsset[]>([]);

  // Platform
  const [platformContractId, setPlatformContractId] = useState<string | null>(null);
  const [platformInitialized, setPlatformInitialized] = useState(false);

  const sdk = useMemo(() => getSDK({ debug: true }), []);
  const service = useMemo(() => new RWAService(packageId, sdk, publicParty || undefined, operatorParty || undefined), [packageId, sdk, publicParty, operatorParty]);
  const settlingRef = useRef(false);

  const loadData = useCallback(async (currentPartyId: string) => {
    // Skip if packageId not set
    if (!packageId) {
      return;
    }

    try {
      // Check if platform exists and get operator
      const platform = await service.getPlatform();
      if (platform) {
        setPlatformContractId(platform.contractId);
        setPlatformInitialized(true);
        if (!operatorParty) {
          setOperatorParty(platform.payload.operator);
        }
      } else {
        setPlatformInitialized(false);
        setPlatformContractId(null);
      }

      const currentOperatorParty = platform?.payload.operator || operatorParty || '';

      // Detect roles
      const detectedRoles = await service.detectRoles(currentPartyId, currentOperatorParty);
      setRoles(detectedRoles);

      // Load common data (investor view)
      // Each query has a .catch() so one failure doesn't block all state updates
      const [
        assetsData,
        holdingsData,
        balanceData,
        paymentHoldingsData,
        listingsData,
        listingProposalsData,
        tradesData,
        profileData,
        selfProfileData,
      ] = await Promise.all([
        service.getAssets().catch((e: unknown) => { console.warn('[RWA] getAssets failed:', e); return [] as RWAAsset[]; }),
        service.getMyHoldings().catch((e: unknown) => { console.warn('[RWA] getMyHoldings failed:', e); return [] as RWAHolding[]; }),
        service.getMyPaymentBalance().catch((e: unknown) => { console.warn('[RWA] getMyPaymentBalance failed:', e); return 0; }),
        service.getPaymentBalances(currentPartyId).catch((e: unknown) => { console.warn('[RWA] getPaymentBalances failed:', e); return [] as PaymentBalance[]; }),
        service.getListings().catch((e: unknown) => { console.warn('[RWA] getListings failed:', e); return [] as Listing[]; }),
        service.getListingProposals().catch(() => [] as ListingProposal[]),
        service.getMyTrades().catch((e: unknown) => { console.warn('[RWA] getMyTrades failed:', e); return [] as Trade[]; }),
        service.getMyProfile().catch((e: unknown) => { console.warn('[RWA] getMyProfile failed:', e); return null; }),
        service.getMySelfProfile().catch((e: unknown) => { console.warn('[RWA] getMySelfProfile failed:', e); return null; }),
      ]);

      console.log('[RWA] loadData:', { assets: assetsData.length, holdings: holdingsData.length, listings: listingsData.length, proposals: listingProposalsData.length, paymentBalance: balanceData, paymentHoldings: paymentHoldingsData.length });

      setAssets(assetsData);
      setMyHoldings(holdingsData);
      setMyPaymentBalance(balanceData);
      setMyPaymentHoldings(paymentHoldingsData);
      setListings(listingsData);
      setListingProposals(listingProposalsData);
      setMyTrades(tradesData);
      // Use self-profile if no operator-verified profile
      setMyProfile(profileData || selfProfileData);

      // Load operator data if has operator role
      if (detectedRoles.isOperator) {
        const [
          licensesData,
          proposalsData,
          depositRequestsData,
          allTradesData,
          profilesData,
          platformData,
        ] = await Promise.all([
          service.getIssuerLicenses().catch(() => [] as IssuerLicense[]),
          service.getAssetProposals().catch(() => [] as AssetProposal[]),
          service.getPaymentDepositRequests().catch(() => [] as PaymentDepositReq[]),
          service.getPendingTrades().catch(() => [] as Trade[]),
          service.getInvestorProfiles().catch(() => [] as InvestorProfile[]),
          service.getPlatform().catch(() => null),
        ]);

        setIssuerLicenses(licensesData);
        setAssetProposals(proposalsData);
        setDepositRequests(depositRequestsData);
        setPendingTrades(allTradesData);
        setAllInvestorProfiles(profilesData);
        if (platformData) setPlatformContractId(platformData.contractId);

        // Auto-settle any pending trades (operator has authority for SettleTrade)
        if (allTradesData.length > 0 && !settlingRef.current) {
          settlingRef.current = true;
          service.autoSettleTrades().then(count => {
            if (count > 0) {
              console.log(`[RWA] Auto-settled ${count} trades`);
              // Don't recursively call loadData — the next 30s refresh will pick up changes
            }
          }).catch(err => console.warn('[RWA] Auto-settle failed:', err))
            .finally(() => { settlingRef.current = false; });
        }
      }

      // Load issuer data if has issuer role
      if (detectedRoles.isIssuer) {
        const issuedAssets = await service.getMyIssuedAssets(currentPartyId).catch(() => [] as RWAAsset[]);
        setMyIssuedAssets(issuedAssets);
      }

      // Load verifier data if has verifier role
      if (detectedRoles.isVerifier) {
        const profilesData = await service.getInvestorProfiles().catch(() => [] as InvestorProfile[]);
        setAllInvestorProfiles(profilesData);
      }

      setError(null);
      setDataLoaded(true);  // Mark that we've successfully loaded data at least once
    } catch (err) {
      console.error('Failed to load RWA data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    }
  }, [service, operatorParty, setOperatorParty, packageId]);

  const refresh = useCallback(async () => {
    if (!partyId) return;
    setLoading(true);
    await loadData(partyId);
    setLoading(false);
  }, [loadData, partyId]);

  // Initial connection
  useEffect(() => {
    if (window.parent === window) {
      setError('This app must be embedded in the Stratos portal');
      setLoading(false);
      return;
    }

    const init = async () => {
      try {
        const state = await sdk.connect();
        setConnected(state.connected);

        if (state.user?.partyId) {
          setPartyId(state.user.partyId);
          await loadData(state.user.partyId);
        }
      } catch (err) {
        console.error('Failed to connect:', err);
        setError(err instanceof Error ? err.message : 'Failed to connect');
      } finally {
        setLoading(false);
      }
    };

    init();

    // Listen for user changes
    sdk.on('userChanged', (newUser) => {
      setPartyId(newUser?.partyId || null);
      setConnected(newUser !== null);
      if (newUser?.partyId) {
        setLoading(true);
        loadData(newUser.partyId).finally(() => setLoading(false));
      }
    });

    return () => sdk.removeAllListeners();
  }, [sdk, loadData]);

  // Reload when packageId becomes available
  useEffect(() => {
    if (packageId && partyId && connected) {
      setLoading(true);
      loadData(partyId).finally(() => setLoading(false));
    }
  }, [packageId, partyId, connected, loadData]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh || !connected || !partyId) return;

    const interval = setInterval(() => loadData(partyId), refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, connected, refreshInterval, loadData, partyId]);

  // ============================================
  // Actions - Investor
  // ============================================

  const transferTokens = useCallback(
    async (holdingContractId: string, recipient: string, amount: number) => {
      const result = await service.transferTokens(holdingContractId, recipient, amount);
      if (partyId) await loadData(partyId);
      return result;
    },
    [service, loadData, partyId]
  );

  const createListingProposal = useCallback(
    async (
      holdingContractId: string,
      asset: { assetId: string; assetSymbol: string; assetName: string; description: string; imageUrl?: string },
      amount: number,
      pricePerToken: number,
      paymentToken: PaymentToken
    ) => {
      const result = await service.createListingProposal(holdingContractId, asset, amount, pricePerToken, paymentToken);
      if (partyId) await loadData(partyId);
      return result;
    },
    [service, loadData, partyId]
  );

  const purchaseFromListing = useCallback(
    async (listingContractId: string, paymentContractId: string, amount: number) => {
      const result = await service.purchaseFromListing(listingContractId, paymentContractId, amount);
      if (partyId) await loadData(partyId);
      return result;
    },
    [service, loadData, partyId]
  );

  const delistAsset = useCallback(
    async (listingContractId: string, sellerHoldingCid: string, amount: number) => {
      await service.delistAsset(listingContractId, sellerHoldingCid, amount);
      if (partyId) await loadData(partyId);
    },
    [service, loadData, partyId]
  );

  const withdrawListingProposal = useCallback(
    async (proposalContractId: string, holdingContractId: string, amount: number) => {
      await service.withdrawListingProposal(proposalContractId, holdingContractId, amount);
      if (partyId) await loadData(partyId);
    },
    [service, loadData, partyId]
  );

  const requestPaymentDeposit = useCallback(
    async (token: PaymentToken, amount: number) => {
      const result = await service.requestPaymentDeposit(token, amount);
      if (partyId) await loadData(partyId);
      return result;
    },
    [service, loadData, partyId]
  );

  // ============================================
  // Actions - Operator
  // ============================================

  const appointIssuer = useCallback(
    async (issuerParty: string, issuerName: string, allowedCategories: AssetCategory[]) => {
      if (!platformContractId) throw new Error('Platform not initialized');
      const result = await service.appointIssuer(platformContractId, issuerParty, issuerName, allowedCategories);
      if (partyId) await loadData(partyId);
      return result;
    },
    [service, loadData, partyId, platformContractId]
  );

  const revokeIssuer = useCallback(
    async (issuerLicenseContractId: string) => {
      if (!platformContractId) throw new Error('Platform not initialized');
      await service.revokeIssuer(platformContractId, issuerLicenseContractId);
      if (partyId) await loadData(partyId);
    },
    [service, loadData, partyId, platformContractId]
  );

  const approveAssetProposal = useCallback(
    async (proposalContractId: string) => {
      const result = await service.approveAssetProposal(proposalContractId);
      if (partyId) await loadData(partyId);
      return result;
    },
    [service, loadData, partyId]
  );

  const rejectAssetProposal = useCallback(
    async (proposalContractId: string, reason: string) => {
      await service.rejectAssetProposal(proposalContractId, reason);
      if (partyId) await loadData(partyId);
    },
    [service, loadData, partyId]
  );

  const approveListingProposal = useCallback(
    async (proposalContractId: string) => {
      const result = await service.approveListingProposal(proposalContractId);
      if (partyId) await loadData(partyId);
      return result;
    },
    [service, loadData, partyId]
  );

  const rejectListingProposal = useCallback(
    async (proposalContractId: string, reason: string) => {
      await service.rejectListingProposal(proposalContractId, reason);
      if (partyId) await loadData(partyId);
    },
    [service, loadData, partyId]
  );

  const approvePaymentDeposit = useCallback(
    async (requestContractId: string) => {
      const result = await service.approvePaymentDeposit(requestContractId);
      if (partyId) await loadData(partyId);
      return result;
    },
    [service, loadData, partyId]
  );

  const settleTrade = useCallback(
    async (tradeContractId: string, sellerPaymentContractId: string) => {
      const result = await service.settleTrade(tradeContractId, sellerPaymentContractId);
      if (partyId) await loadData(partyId);
      return result;
    },
    [service, loadData, partyId]
  );

  const autoSettleTrades = useCallback(
    async () => {
      const count = await service.autoSettleTrades();
      if (partyId && count > 0) await loadData(partyId);
      return count;
    },
    [service, loadData, partyId]
  );

  const unlockTokens = useCallback(
    async (holdingContractId: string, amount: number) => {
      await service.unlockTokens(holdingContractId, amount);
      if (partyId) await loadData(partyId);
    },
    [service, loadData, partyId]
  );

  const archiveStuckTrade = useCallback(
    async (tradeContractId: string) => {
      await service.archiveStuckTrade(tradeContractId);
      if (partyId) await loadData(partyId);
    },
    [service, loadData, partyId]
  );

  // ============================================
  // Actions - Issuer
  // ============================================

  const proposeAsset = useCallback(
    async (assetDetails: {
      assetId: string;
      name: string;
      symbol: string;
      category: AssetCategory;
      assetType: AssetType;
      totalSupply: number;
      compliance: Partial<ComplianceConfig>;
      underlying: Partial<UnderlyingAsset>;
    }) => {
      if (!roles?.issuerLicense) throw new Error('No issuer license');
      if (!operatorParty) throw new Error('Operator party not set');
      if (!publicParty) throw new Error('Public party not set');
      const result = await service.proposeAsset(operatorParty, publicParty, assetDetails);
      if (partyId) await loadData(partyId);
      return result;
    },
    [service, loadData, partyId, roles, operatorParty, publicParty]
  );

  const mintTokens = useCallback(
    async (assetContractId: string, recipient: string, amount: number) => {
      const result = await service.mintTokens(assetContractId, recipient, amount);
      if (partyId) await loadData(partyId);
      return result;
    },
    [service, loadData, partyId]
  );

  const mintNFT = useCallback(
    async (assetContractId: string, recipient: string) => {
      const result = await service.mintNFT(assetContractId, recipient);
      if (partyId) await loadData(partyId);
      return result;
    },
    [service, loadData, partyId]
  );

  // ============================================
  // Actions - Verifier
  // ============================================

  const verifyInvestor = useCallback(
    async (
      investorParty: string,
      status: 'Unverified' | 'KYCVerified' | 'AccreditedVerified' | 'InstitutionalVerified',
      jurisdiction: string,
      expiryDate?: string
    ) => {
      if (!roles?.verifierContract) throw new Error('No verifier contract');
      const result = await service.verifyInvestor(
        roles.verifierContract.contractId,
        investorParty,
        status,
        jurisdiction,
        expiryDate
      );
      if (partyId) await loadData(partyId);
      return result;
    },
    [service, loadData, partyId, roles]
  );

  const initializePlatform = useCallback(
    async (config: { platformName: string; platformFeePercent: string; feeCollector: string; operator: string; publicParty: string }) => {
      const contractId = await service.initializePlatform(config);
      if (partyId) await loadData(partyId);
      return contractId;
    },
    [service, loadData, partyId]
  );

  const selfRegisterAsInvestor = useCallback(
    async (operatorParty: string, jurisdiction: string = 'US') => {
      const profileId = await service.selfRegisterAsInvestor(operatorParty, jurisdiction);
      if (partyId) await loadData(partyId);
      return profileId;
    },
    [service, loadData, partyId]
  );

  return {
    connected,
    partyId,
    loading,
    dataLoaded,
    error,
    roles,
    assets,
    myHoldings,
    myPaymentBalance,
    myPaymentHoldings,
    listings,
    myTrades,
    myProfile,
    issuerLicenses,
    assetProposals,
    listingProposals,
    depositRequests,
    pendingTrades,
    allInvestorProfiles,
    myIssuedAssets,
    platformContractId,
    platformInitialized,
    initializePlatform,
    selfRegisterAsInvestor,
    refresh,
    transferTokens,
    createListingProposal,
    purchaseFromListing,
    delistAsset,
    withdrawListingProposal,
    requestPaymentDeposit,
    appointIssuer,
    revokeIssuer,
    approveAssetProposal,
    rejectAssetProposal,
    approveListingProposal,
    rejectListingProposal,
    approvePaymentDeposit,
    settleTrade,
    autoSettleTrades,
    unlockTokens,
    archiveStuckTrade,
    proposeAsset,
    mintTokens,
    mintNFT,
    verifyInvestor,
  };
}
